import { autoEffect, store as createStore } from '@risingstack/react-easy-state'
import Axios from 'axios'
import api from '../api'
import { read, write } from '../utilities/local-storage'
import { debug } from '../utilities/logger'
import config from '../config'

/**
 * [Store]
 */
const store = createStore({
  email: '',
  registration: {
    email: '',
    firstName: '',
    lastName: '',
    newsletter: false,
    birthdate: null,
    nationality: 'fr',
    phone: null,
    country: 'fr',
    address: null,
    couponCodeId: null,
  },
  token: {
    access: null,
    type: null,
    refresh: null,
    rememberMe: false,
    expiration: null
  },
  initialized: false,
  initialize,
  isAuthententicated,
  askResetPassword,
  authenticate,
  canRefresh,
  resetPassword,
  refresh,
  register,
  connectAs,
  logout
})

/**
 * [Cache management & others]
 */
const AUTHENTICATION_STORAGE_KEY = 'stores.authentication.token'
function load () { return read(AUTHENTICATION_STORAGE_KEY) }
function persist () { write(AUTHENTICATION_STORAGE_KEY, store.token) }

async function loadCache () {
  debug('[store][authentication.loadCache]')
  const cache = load()
  store.token = { ...store.token, ...cache }
}

function enableAutoPersist () {
  autoEffect(() => {
    debug('[store][authentication.autoeffect]')
    if (isAuthententicated()) {
      Axios.defaults.headers.common['Authorization'] = `${store.token.type} ${store.token.access}`
    }
    persist()
  })
}

function enableAutoRefresh () {
  setInterval(() => refresh(), config.authentication_refresh_interval)
}

async function initialize () {
  if (store.initialized) { return }
  store.initialized = true
  loadCache()
  enableAutoPersist()
  enableAutoRefresh()
}

/**
 * [Functions]
 */
function isTokenExpired ({ delta } = { delta: 0 }) {
  let expiration = store.token ? store.token.expiration - delta : null
  if (expiration) {
    return (+expiration < Math.floor(Date.now() / 1000))
  }
  return true
}

function isAuthententicated () {
  if (store.token.access && !isTokenExpired()) {
    return true
  }
  return false
}

function canRefresh () {
  return isTokenExpired({ delta: config.authentication_refresh_delta }) && store.token.refresh
}

async function refresh () {
  debug(`[stores][authentication.refresh] Checking refresh status`)
  if (canRefresh()) {
    debug(`[stores][authentication.refresh] Refreshing token`)
    try {
      const response = await api.authentication.refresh({ refresh: store.token.refresh })
      storeAuthentication({ token: response.data, rememberMe: true })
    } catch (e) {
      debug(`[stores][authentication.refresh] Unable to refresh token. Logging out.`)
      logout()
    }
  }
}

async function askResetPassword () {
  return api.authentication.askResetPassword({
    email: store.email
  })
}

async function logout () {
  if (store.token.asAdmin) {
    api.authentication.disconnectConnectedAs()
  }
  store.token = {
    ...store.token,
    access: null,
    type: null,
    refresh: null,
    expiration: null,
    asAdmin: null,
    email: null
  }
}

function storeAuthentication ({ token, rememberMe }) {
  store.token.rememberMe = rememberMe
  store.token.access = token.access_token
  store.token.refresh = rememberMe ? token.refresh_token : undefined
  store.token.type = token.token_type
  store.token.expiration = Math.floor(Date.now() / 1000) + token.expires_in
  store.token.asAdmin = null
  store.token.email = null
}

async function authenticate ({ password, rememberMe }) {
  const response = await api.authentication.authenticate({
    email: store.email,
    password: password,
    type: 'password'
  })
  storeAuthentication({ token: response.data, rememberMe })
}

async function register ({ password, referralCode, proBusinessReferralCode }) {
  await api.authentication.register(store.registration, { password, referralCode, proBusinessReferralCode })
  const response = await api.authentication.authenticate({
    email: store.registration.email,
    password,
    referralCode,
    type: !password ? 'referral_code' : 'password'
  })
  storeAuthentication({ token: response.data, rememberMe: true })
}

async function connectAs ({ access_token, token_type, expires_in, email }) {
  storeAuthentication({ token: { access_token, token_type, expires_in }, rememberMe: false })
  store.token.asAdmin = true
  store.token.email = email
}

async function resetPassword ({ role, key, password }) {
  const user = await api.authentication.resetPassword({ role, key, password })
  if (role.indexOf(config.customers) !== -1) {
    store.email = user.email
    await authenticate({ email: user.email, password })
  }
}

export default store



// WEBPACK FOOTER //
// ./src/stores/authentication.js